import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import classnames from "classnames";
import classes from "../CalendarWeekView/CalendarWeekView.module.css";
import { useLocation } from "react-router-dom";
import {
  useCalendarDateUrl,
  useReftchRefProvider,
  weekDayTranslation,
  useCalendarDayDateUrl,
  useAgendaDayDateUrl,
  AgendaSelectedUserContext,
  useTarefasDateUrl,
  useOutlookEventsDateUrl,
  outlookMutateBeforeNormalization,
} from "../../CalendarHelper";
import { FormattedMessage } from "react-intl";
import { many } from "../../../../Helpers/SchemaHelper";
import { useToast } from "../../../Toast/ToastProvider";
import { useQuery, useSpaceQuery } from "../../../../Helpers/IOClient";
import { useMultipleEntityValueSelector } from "../../../../Hooks/AdvancedEntityHooks";
import {
  buildItems,
  buildResolvedValueItem,
  sortRowItems,
} from "../CalendarMonthView/CalendarMonthView";
import { BarLoader } from "../../../GlobalLoader/GlobalLoader";
import {
  buildResolvedWeeklyValueItem,
  CalendarHourList,
  WeeklyContentGridColumn,
  HourSelectorContext,
  UpdatingItemsProvider,
  IntermediaryBarLoader,
} from "../CalendarWeekView/CalendarWeekView";
import moment from "moment";
import {
  spaceInterventionsSchema,
  spaceOutlookEventsSchema,
  spaceTasksSchema,
} from "../../../../config/schema";
import { useAgendaPreferences } from "../../../AgendaPreferences/AgendaPreferences";
import { MilSecToFormatedHourMinute } from "../../../../Helpers/MiscHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/pro-light-svg-icons";

const dayMilsec = 86400000;

const WeekDayHeader = React.memo(function WeekDayHeader({
  startMil,
  holidayData,
  items,
}) {
  const personalPreferences = useAgendaPreferences();
  const { dayOfWeek, day, isCurrentDay, isHoliday, IsWeekend } = useMemo(() => {
    const d = new Date(startMil);
    let isHoliday = false;
    let IsWeekend = false;
    const currentDate = new Date();
    const isCurrentDay = currentDate.toDateString() === d.toDateString();

    if (Array.isArray(holidayData)) {
      const resolvedPersonalPreferences = personalPreferences || {};
      const momentDate = moment(d);
      const resolvedString = momentDate.format("YYYY-MM-DD");
      const weekDay = momentDate.day();
      const dayObj =
        holidayData.find((h) => {
          const key = h?.DateKey || "";
          return key === resolvedString;
        }) || {};
      isHoliday = dayObj.IsHoliday;
      IsWeekend = dayObj.IsWeekend;
      if (Array.isArray(resolvedPersonalPreferences.workschedule)) {
        IsWeekend = !resolvedPersonalPreferences.workschedule[weekDay]?.enabled;
      }
    }

    return {
      dayOfWeek: d.getDay(),
      day: d.getDate(),
      isCurrentDay,
      isHoliday,
      IsWeekend,
    };
  }, [holidayData, personalPreferences, startMil]);

  const total = useMemo(() => {
    if (!Array.isArray(items)) return null;
    let t = 0;
    for (const e of items) {
      const { metadata } = e;
      if (metadata?.EstimatedCost && !metadata?.IsTask) {
        t += metadata.EstimatedCost;
      } 
      // else if (metadata?.Duration) {
      //   t += metadata.Duration;
      // }
    }
    return `${MilSecToFormatedHourMinute(t)}`;
  }, [items]);

  const location = useLocation();

  const isOnAgenda = useMemo(() => {
    return (
      location.pathname.includes("calendar") ||
      location.pathname.includes("timemanager")
    );
  }, [location.pathname]);

  return (
    <div className={classnames("d-flex", classes.headerContainer)}>
      <div className={classnames("d-flex flex-1")}>
        <div
          className={classnames(
            "d-flex justify-content-center gap-1 text-black text-uppercase fs-14",
            classes.headerDay,
            isOnAgenda && isHoliday ? classes.holidayItemBackground : "",
            isOnAgenda && IsWeekend ? classes.weekendItemBackground : "",
            {
              [classes.selected]: isCurrentDay,
            },
          )}
        >
          <span className="fw-medium h-fit">
            <FormattedMessage id={weekDayTranslation[dayOfWeek]} /> {day}
          </span>
          {total ? (
            <div className="d-flex h-fit flex-0-0-auto ml-3 align-items-center">
              <FontAwesomeIcon icon={faClock} />
              <span className="ml-1">{total}</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className={classes.fakeScroll} />
    </div>
  );
});

const getAllWeekDays = (year, month, day) => {
  const startDate = new Date(year, month, day);
  const endDate = new Date(year, month, day + 1);

  return { startDate, endDate, month, startDateMil: startDate.getTime() };
};
const getCurrentDay = (year, month, day) => {
  const startDate = new Date(year, month, day);
  const endDate = new Date(year, month, day);

  return { startDate, endDate, month, startDateMil: startDate.getTime() };
};

const baseHeight = 60;

const ContentGrid = ({
  startDateMil,
  items,
  settings,
  containerRef,
  holidayData,
}) => {
  const currentDate = useMemo(() => {
    const date = new Date();
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }, []);

  const [selectedDate, setSelectedDate] = useState({
    timeA: null,
    timeB: null,
  });

  const { gridItems } = items;
  const timeoutRef = useRef();

  const [currentTimePos, setCurrentTimePos] = useState(() => {
    const currentTime = moment();
    const startMil = currentTime.hours();
    return startMil * baseHeight + currentTime.minutes();
  });

  useEffect(() => {
    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
    }
    timeoutRef.current = setInterval(() => {
      const currentTime = moment();
      const startMil = currentTime.hours();
      setCurrentTimePos(startMil * baseHeight + currentTime.minutes());
    }, 60000);

    return () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex-1 position-relative d-flex">
      <div
        style={{ top: currentTimePos }}
        className={classes.currentTimeBar}
      ></div>
      <HourSelectorContext.Provider value={[selectedDate, setSelectedDate]}>
        <WeeklyContentGridColumn
          containerRef={containerRef}
          settings={settings}
          holidayData={holidayData}
          startDateMil={startDateMil}
          i={0}
          currentDate={currentDate}
          items={gridItems}
        />
      </HourSelectorContext.Provider>
    </div>
  );
};

const selector = (v) => {
  return v;
};

const useDayGridItems = (data, startDate, settings) => {
  return useMemo(() => {
    const gridItems = [];
    if (!data || data.length === 0) return { gridItems };

    const resolvedResults = data.map((v) =>
      buildResolvedWeeklyValueItem(v, settings, startDate),
    );

    const outOfGridItems = [];

    for (const item of resolvedResults) {
      const { epochStart, epochEnd } = item;

      if (epochEnd - epochStart >= dayMilsec) {
        outOfGridItems.push(item);
        continue;
      }

      gridItems.push(item);
    }

    // console.log(gridItems);

    return { gridItems, outOfGridItems };
  }, [data, settings, startDate]);
};

const ItemsHeader = ({
  outOfGridItems,
  settings,
  startDate,
  overrideNumberofOutofBound,
  gridRowEndDay,
}) => {
  const x = useMemo(() => {
    const x = outOfGridItems.map((v) =>
      buildResolvedValueItem(v.metadata, settings, startDate),
    );
    return sortRowItems(x, 0);
  }, [outOfGridItems, settings, startDate]);

  const { children, elapsedDays } = buildItems(
    x,
    0,
    overrideNumberofOutofBound,
    startDate,
    settings,
    {
      isDaily: true,
      gridRowEndDay,
    },
  );



  return (
    <div
      className="d-flex border-bottom"
      style={
        {
          // padding: "4px 0px 0px 52px",
        }
      }
    >
      <div
        className={classnames(
          classes.hourList,
          classes.hourItem,
          "h-100 border-0",
        )}
      ></div>
      <div
        className="flex-1"
        style={{
          position: "relative",
          height: 29 * elapsedDays,
          margin: "4px 0px 0px 0px",
        }}
      >
        {children}
      </div>
      <div className={classes.fakeScroll} />
    </div>
  );
};

const InnerCalendarViewAgenda = ({
  dateInfo,
  settings,
  data,
  loading,
  holidayData,
}) => {
  const { startDateMil, startDate, endDate } = dateInfo;

  const { schema } = settings;
  const connectedResults = useMultipleEntityValueSelector(
    schema,
    data?.appointments || [],
    selector,
  );
  const connectedResults2 = useMultipleEntityValueSelector(
    spaceInterventionsSchema,
    data?.times || [],
    selector,
  );

  const connectedResults3 = useMultipleEntityValueSelector(
    spaceOutlookEventsSchema,
    data?.outlookEvents || [],
    selector,
  );

  const connectedResults4 = useMultipleEntityValueSelector(
    spaceTasksSchema,
    data?.tasks || [],
    selector,
  );

  const resolvedConnectedResults = useMemo(
    () => [
      ...connectedResults,
      ...connectedResults2,
      ...connectedResults3,
      ...connectedResults4,
    ],
    [connectedResults, connectedResults2, connectedResults3, connectedResults4],
  );

  const items = useDayGridItems(resolvedConnectedResults, startDate, settings);
 
  const outOfGridItems = useMemo(() => {
    return items && Array.isArray(items.outOfGridItems) ? [...items.outOfGridItems] : [];
  }, [items]);

  const containerRef = useRef();

  

  return (
    <div
      className={classnames(
        "flex-1 w-100 d-flex flex-column overflow-hidden position-relative rounded",
        classes.weekView,
      )}
    >
      <IntermediaryBarLoader isLoading={loading} />
      <WeekDayHeader
        items={items?.gridItems}
        startMil={startDateMil}
        holidayData={holidayData}
      />
      {outOfGridItems && outOfGridItems.length > 0 && (
        <ItemsHeader
          outOfGridItems={outOfGridItems}
          startDate={startDate}
          gridRowEndDay={endDate}
          overrideNumberofOutofBound={100}
          settings={settings}
        />
      )}
      <div
        ref={containerRef}
        id="calendarContainerScroll"
        className={classnames("w-100 flex-1", classes.gridContainer)}
      >
        <div className="d-flex w-100">
          <CalendarHourList />
          <ContentGrid
            containerRef={containerRef}
            settings={settings}
            holidayData={holidayData}
            items={items}
            startDateMil={startDateMil}
          />
        </div>
      </div>
    </div>
  );
};

const InnerCalendarView = ({
  dateInfo,
  settings,
  data,
  loading,
  holidayData,
}) => {
  const { startDateMil, startDate, endDate } = dateInfo;

  const { schema } = settings;
  const connectedResults = useMultipleEntityValueSelector(
    schema,
    data,
    selector,
  );

  const location = useLocation();
  const isOnCalendar = useMemo(() => {
    return location.pathname.includes("/calendar");
  }, [location.pathname]);

  const items = useDayGridItems(connectedResults, startDate, settings);

  const { outOfGridItems } = items;
  const containerRef = useRef();

  console.log(outOfGridItems)

  return (
    <div
      className={classnames(
        "flex-1 w-100 d-flex flex-column overflow-hidden position-relative rounded",
        classes.weekView,
      )}
    >
      <IntermediaryBarLoader isLoading={loading} />
      <WeekDayHeader
        items={items?.gridItems}
        startMil={startDateMil}
        holidayData={holidayData}
      />
      {outOfGridItems && outOfGridItems.length > 0 && (
        <ItemsHeader
          outOfGridItems={outOfGridItems}
          startDate={startDate}
          gridRowEndDay={endDate}
          overrideNumberofOutofBound={isOnCalendar ? 100 : undefined}
          settings={settings}
        />
      )}
      <div
        ref={containerRef}
        id="calendarContainerScroll"
        className={classnames("w-100 flex-1", classes.gridContainer)}
      >
        <div className="d-flex w-100">
          <CalendarHourList />
          <ContentGrid
            containerRef={containerRef}
            settings={settings}
            holidayData={holidayData}
            items={items}
            startDateMil={startDateMil}
          />
        </div>
      </div>
    </div>
  );
};

const CalendarDailyView = ({ selectedDate, settings }) => {
  // const [resolvedDate, setResolvedDate] = useState(selectedDate);
  const refetchRef = useReftchRefProvider();
  const dateInfo = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    return getCurrentDay(year, month, day);
  }, [selectedDate]);

  const url = useCalendarDayDateUrl(dateInfo, settings);

  const { schema } = settings;
  const RSchema = useMemo(() => {
    if (schema.name === "SpaceAgenda") {
      return {
        results: {
          Appointments: many(schema),
          Times: many(spaceInterventionsSchema),
        },
      };
    } else
      return {
        d: {
          results: many(schema),
        },
        results: many(schema),
      };
  }, [schema]);

  const holidaysUrl = useMemo(() => {
    if (!dateInfo) return null;
    const startDateString = moment(dateInfo.startDate).format("YYYY-MM-DD");
    const endDateString = moment(dateInfo.endDate).format("YYYY-MM-DD");
    return `interventions/report?fromdate=${startDateString}&todate=${endDateString}`;
  }, [dateInfo]);

  const {
    data: holidayData,
    loading: loadingHolidays,
    refetch: refetchHolidays,
  } = useSpaceQuery(holidaysUrl, null);

  const createToast = useToast();
  const [data, setData] = useState([]);

  useEffect(() => {
    const { communicator } = settings;
    if (!communicator) return;

    const unsub = communicator.subscribe((e) => {
      const { type, id } = e;
      if (type !== "creation") return;
      setData((d) => [...d, id]);
      // console.log(v);
    });
    return unsub;
  }, [settings]);

  const { loading, refetch } = useQuery(url, RSchema, {
    onSuccess: ({ data }) => {
      if (data?.results?.Appointments || data?.results?.Times) {
        setData(() => {
          const resolvedApp = Array.isArray(data?.results?.Appointments)
            ? data?.results?.Appointments
            : [];
          const resolvedTimes = Array.isArray(data?.results?.Times)
            ? data?.results?.Times
            : [];
          return {
            appointments: resolvedApp,
            times: resolvedTimes,
          };
        });
      } else {
        if (data?.d?.results) setData(data.d.results);
        else setData(data.results);
      }
    },
    onError: () => {
      createToast({
        pos: "tm",
        type: "danger",
        description: <FormattedMessage id="CALENDAR_GENERAL_ERROR" />,
        title: <FormattedMessage id="ERROR" />,
      });
    },
    cache: false,
  });

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch, refetchRef]);

  if (schema.name === "SpaceAgenda") {
    return (
      <UpdatingItemsProvider>
        <InnerCalendarViewAgenda
          loading={loading || loadingHolidays}
          holidayData={holidayData}
          // loading={false}
          settings={settings}
          data={data}
          dateInfo={dateInfo}
        />
      </UpdatingItemsProvider>
    );
  }

  return (
    <UpdatingItemsProvider>
      <InnerCalendarView
        loading={loading || loadingHolidays}
        settings={settings}
        data={data}
        holidayData={holidayData}
        dateInfo={dateInfo}
      />
    </UpdatingItemsProvider>
  );
};

export const AgendaDailyView = ({ selectedDate, settings }) => {
  // const [resolvedDate, setResolvedDate] = useState(selectedDate);
  const refetchRef = useReftchRefProvider();

  const notesRefetchRef = useRef(null);
  const interventionRef = useRef(null);
  const outlookEventsRef = useRef(null);
  const tarefasRef = useRef(null);
  const holidayRef = useRef(null);

  const dateInfo = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    return getCurrentDay(year, month, day);
  }, [selectedDate]);
  const selectedUser = useContext(AgendaSelectedUserContext);
  const notesUrl = useAgendaDayDateUrl(dateInfo, settings);

  const TimeSettings = useMemo(() => {
    return {
      ...settings.extraTimeChargeSettings,
      extraInbuiltFilter: [
        {
          operator: "eq",
          field: "Account/Id",
          value: selectedUser,
          data: {
            type: "string",
          },
        },
      ],
    };
  }, [selectedUser, settings.extraTimeChargeSettings]);

  const timesUrl = useCalendarDateUrl(dateInfo, TimeSettings);

  const { schema, extraTimeChargeSettings } = settings;

  const RSchema = useMemo(() => {
    return {
      d: {
        results: many(schema),
      },
      results: many(schema),
    };
  }, [schema]);

  const Interventionschema = useMemo(() => {
    return {
      d: {
        results: many(extraTimeChargeSettings.schema),
      },
      results: many(extraTimeChargeSettings.schema),
    };
  }, [extraTimeChargeSettings]);

  const holidaysUrl = useMemo(() => {
    if (!dateInfo) return null;
    const startDateString = moment(dateInfo.startDate).format("YYYY-MM-DD");
    const endDateString = moment(dateInfo.endDate).format("YYYY-MM-DD");
    return `interventions/report?fromdate=${startDateString}&todate=${endDateString}`;
  }, [dateInfo]);

  const {
    data: holidayData,
    loading: loadingHolidays,
    refetch: refetchHolidays,
  } = useSpaceQuery(holidaysUrl, null);

  const createToast = useToast();
  const [data, setData] = useState([]);

  useEffect(() => {
    const { communicator } = settings;
    if (!communicator) return;

    const unsub = communicator.subscribe((e) => {
      const { type, id } = e;
      if (type !== "creation") return;
      setData((d) => [...d, id]);
      // console.log(v);
    });
    return unsub;
  }, [settings]);

  const { loading, refetch: refetchNotes } = useQuery(notesUrl, RSchema, {
    onSuccess: ({ data }) => {
      if (data.results) {
        setData((old) => {
          const resolvedApp = Array.isArray(data.results) ? data.results : [];
          return {
            ...old,
            appointments: resolvedApp,
          };
        });
      }
    },
    onError: () => {
      createToast({
        pos: "tm",
        type: "danger",
        description: <FormattedMessage id="CALENDAR_GENERAL_ERROR" />,
        title: <FormattedMessage id="ERROR" />,
      });
    },
    cache: false,
  });

  const { loading: loadingTimes, refetch: refetchTimes } = useQuery(
    timesUrl,
    Interventionschema,
    {
      onSuccess: ({ data }) => {
        if (data?.d?.results) {
          setData((old) => {
            const resolvedApp = Array.isArray(data.d.results)
              ? data.d.results
              : [];
            return {
              ...old,
              times: resolvedApp,
            };
          });
        }
      },
      onError: () => {
        createToast({
          pos: "tm",
          type: "danger",
          description: <FormattedMessage id="CALENDAR_GENERAL_ERROR" />,
          title: <FormattedMessage id="ERROR" />,
        });
      },
      cache: false,
    },
  );

  const OutlookEventsSchema = useMemo(() => {
    return {
      d: {
        results: many(spaceOutlookEventsSchema),
      },
      results: many(spaceOutlookEventsSchema),
    };
  }, []);

  const outlookEventsUrl = useOutlookEventsDateUrl(dateInfo, {
    schema: spaceOutlookEventsSchema,
  });

  const { loading: loadingOutlookEvents, refetch: refetchOutlookEvents } =
    useQuery(
      // notesUrl, Noteschema,
      // notesUrl,
      outlookEventsUrl,
      OutlookEventsSchema,
      {
        mutateBeforeNormalization: outlookMutateBeforeNormalization,
        onSuccess: ({ data }) => {
          if (data?.results) {
            setData((old) => {
              const resolvedApp = Array.isArray(data?.results)
                ? data?.results
                : [];

              return {
                ...old,
                outlookEvents: resolvedApp,
              };
            });
          }
        },
        onError: () => {
          setData((old) => {
            return {
              ...old,
              outlookEvents: [],
            };
          });
          createToast({
            pos: "tm",
            type: "danger",
            description: <FormattedMessage id="CALENDAR_GENERAL_ERROR" />,
            title: <FormattedMessage id="ERROR" />,
          });
        },
        cache: false,
      },
    );

  const TarefasSchemas = useMemo(() => {
    return many(spaceTasksSchema);
  }, []);

  const tarefasUrl = useTarefasDateUrl(dateInfo);

  const { loading: loadingTaskEvents, refetch: refetchTaskEvents } = useQuery(
    tarefasUrl,
    TarefasSchemas,
    {
      mutateBeforeNormalization: (d) => {
        if (Array.isArray(d)) {
          return d.map((item) => {
            return {
              ...item,
              IsTask: true,
            };
          });
        } else return d;
      },
      onSuccess: ({ data }) => {
        if (Array.isArray(data)) {
          setData((old) => {
            return {
              ...old,
              tasks: data,
            };
          });
        }
      },
      onError: () => {
        setData((old) => {
          return {
            ...old,
            tasks: [],
          };
        });
        createToast({
          pos: "tm",
          type: "danger",
          description: <FormattedMessage id="CALENDAR_GENERAL_ERROR" />,
          title: <FormattedMessage id="ERROR" />,
        });
      },
      cache: false,
    },
  );

  useEffect(() => {
    interventionRef.current = refetchTimes;
    notesRefetchRef.current = refetchNotes;
    outlookEventsRef.current = refetchOutlookEvents;
    tarefasRef.current = refetchTaskEvents;
    holidayRef.current = refetchHolidays;
    refetchRef.current = () => {
      interventionRef.current();
      notesRefetchRef.current();
      outlookEventsRef.current();
      tarefasRef.current();
      holidayRef.current();
    };
  }, [
    refetchTimes,
    refetchNotes,
    refetchRef,
    refetchOutlookEvents,
    refetchTaskEvents,
    refetchHolidays,
  ]);

  if (schema.name === "SpaceAgenda") {
    return (
      <UpdatingItemsProvider>
        <InnerCalendarViewAgenda
          loading={
            loading ||
            loadingHolidays ||
            loadingTimes ||
            loadingOutlookEvents ||
            loadingTaskEvents
          }
          holidayData={holidayData}
          // loading={false}
          settings={settings}
          data={data}
          dateInfo={dateInfo}
        />
      </UpdatingItemsProvider>
    );
  }

  return (
    <UpdatingItemsProvider>
      <InnerCalendarView
        loading={
          loading ||
          loadingHolidays ||
          loadingTimes ||
          loadingOutlookEvents ||
          loadingTaskEvents
        }
        settings={settings}
        data={data}
        holidayData={holidayData}
        dateInfo={dateInfo}
      />
    </UpdatingItemsProvider>
  );
};

export default CalendarDailyView;
